import type { ReactNode } from "react";
import { Link } from "react-router";
import { ChevronRight, Compass, type LucideIcon } from "lucide-react";
import { SectionCard } from "@/components/common";
import { focusRingInsetClass } from "@/components/ui";
import { cn } from "@/lib/utils";

interface AdminUserShortcutBase {
  key: string;
  icon: LucideIcon;
  /** Tinted icon tile classes (bg/text/ring). */
  tone: string;
  label: string;
  meta?: ReactNode;
}

/** Either a router link (`to`) or an in-page action (`onSelect`, e.g. switch tab). */
export type AdminUserShortcut = AdminUserShortcutBase &
  (
    | { to: string; onSelect?: never }
    | { onSelect: () => void; to?: never }
  );

interface AdminUserShortcutsProps {
  title: string;
  items: AdminUserShortcut[];
  className?: string;
}

const rowClass = cn(
  "group flex w-full items-center gap-3 px-5 py-3 text-left transition-colors hover:bg-surface-50 dark:hover:bg-surface-100/50",
  focusRingInsetClass,
);

function ShortcutBody({ item }: { item: AdminUserShortcut }) {
  const Icon = item.icon;
  return (
    <>
      <span
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset",
          item.tone,
        )}
      >
        <Icon
          className="h-4 w-4"
          aria-hidden
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-surface-900 block truncate text-sm font-medium">
          {item.label}
        </span>
        {item.meta ? (
          <span className="text-surface-500 mt-0.5 block truncate text-[13px]">
            {item.meta}
          </span>
        ) : null}
      </span>
      <ChevronRight
        className="text-surface-400 h-4 w-4 shrink-0 transition-transform group-hover:translate-x-0.5"
        aria-hidden
      />
    </>
  );
}

/** Compact "jump to" rail for the user detail overview (other tabs, related pages). */
export function AdminUserShortcuts({
  title,
  items,
  className,
}: AdminUserShortcutsProps) {
  return (
    <SectionCard
      title={title}
      icon={Compass}
      className={className}
      contentClassName="p-0 space-y-0"
    >
      <ul className="divide-surface-100 divide-y">
        {items.map((item) => (
          <li key={item.key}>
            {item.to !== undefined ? (
              <Link
                to={item.to}
                className={rowClass}
              >
                <ShortcutBody item={item} />
              </Link>
            ) : (
              <button
                type="button"
                onClick={item.onSelect}
                className={rowClass}
              >
                <ShortcutBody item={item} />
              </button>
            )}
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}
